import { useMemo } from 'react';
import { Box, Tabs, Tab } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import LoadComponent from './LoadComponent';
import LoadBuild from './LoadBuild';
import LoadKits from './LoadKits';

export const tabContents = [{ label: 'Build' }, { label: 'Kits' }, { label: 'Components' }];

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

LoadTabGroup.propTypes = {
  rackName: PropTypes.string,
  systemType: PropTypes.string,
  buildID: PropTypes.string,
  setBuildID: PropTypes.func,
  buildDetails: PropTypes.object,
  setBuildDetails: PropTypes.func,
  kitList: PropTypes.object,
  setKitList: PropTypes.func,
  componentsList: PropTypes.object,
  setComponentsList: PropTypes.func,
  tabValueRef: PropTypes.number,
  setTabValueRef: PropTypes.func,
  provision: PropTypes.bool,
  kitSystemType: PropTypes.string,
  setKitSystemType: PropTypes.func,
  selectedSnapshot: PropTypes.object
};

export function LoadTabGroup({
  rackName,
  systemType,
  kitList,
  setKitList,
  componentsList,
  setComponentsList,
  buildID,
  setBuildID,
  buildDetails,
  setBuildDetails,
  tabValueRef,
  setTabValueRef,
  provision,
  kitSystemType,
  setKitSystemType,
  selectedSnapshot
}) {
  const dispatch = useDispatch();
  const identity = useSelector((state) => state.identity.value);

  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const handleTabChange = (event, newValue) => {
    setTabValueRef(newValue);
  };

  const COMPONENT_KEYS = ['software', 'content', 'media', 'onemedia'];

  // filters the snapshot rack details loading history to only include kits with completed status, as well as components with completed status.
  const filterCompletedComponents = (item) => {
    const updated = { ...item };

    COMPONENT_KEYS.forEach((key) => {
      if (item[key]?.components?.length) {
        updated[key] = {
          ...item[key],
          components: item[key].components.filter((c) => c.status === 'completed')
        };
      }
    });

    const hasComponents = COMPONENT_KEYS.some((key) => updated[key]?.components?.length);
    return hasComponents ? updated : null;
  };

  const filterCompleteLoadingHistory = (snapshot) => {
    const loadingHistory = snapshot?.rack_details?.loading_history;
    if (!loadingHistory) return snapshot;

    const kits = loadingHistory.kits?.filter((k) => k.status === 'completed') || [];

    const components = (loadingHistory.components || [])
      .map(filterCompletedComponents)
      .filter(Boolean);

    return {
      ...snapshot,
      rack_details: {
        ...snapshot.rack_details,
        loading_history: {
          ...loadingHistory,
          kits,
          components
        }
      }
    };
  };

  const selectedSnapshotFilteredLoadingHistory = useMemo(() => {
    const cleaned = filterCompleteLoadingHistory(selectedSnapshot);
    return cleaned;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSnapshot]);

  return (
    <Box>
      <Box data-testid="load-tab-group" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          textColor="primary"
          indicatorColor="primary"
          value={tabValueRef}
          onChange={handleTabChange}
        >
          {tabContents.map((tab) => (
            <Tab data-testid={`${tab.label}-tab`} key={tab.label} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      {tabContents.map((tab, i) => (
        <TabPanel key={i} value={tabValueRef} index={i}>
          {tab.label === 'Build' && (
            <LoadBuild
              buildID={buildID}
              setBuildID={setBuildID}
              buildDetails={buildDetails}
              setBuildDetails={setBuildDetails}
              kitList={kitList}
              setKitList={setKitList}
            />
          )}
          {tab.label === 'Kits' && (
            <LoadKits
              kitList={kitList}
              setKitList={setKitList}
              openSnackbar={openSnackbar}
              identity={identity}
              rackName={rackName}
              systemType={systemType}
              provision={provision}
              setKitSystemType={setKitSystemType}
              kitSystemType={kitSystemType}
              selectedSnapshot={selectedSnapshotFilteredLoadingHistory}
            />
          )}
          {tab.label === 'Components' && (
            <LoadComponent
              componentsList={componentsList}
              setComponentsList={setComponentsList}
              openSnackbar={openSnackbar}
              identity={identity}
              rackName={rackName}
              systemType={systemType}
              selectedSnapshot={selectedSnapshotFilteredLoadingHistory}
            />
          )}
        </TabPanel>
      ))}
    </Box>
  );
}
